import { useId } from "react";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogContentText from "@mui/material/DialogContentText";
import DialogActions from "@mui/material/DialogActions";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import type { JSX, ReactNode } from "react";

/**
 * FormDialog — оболочка диалога формы: заголовок, подзаголовок с контекстом
 * решения, содержимое, действия и состояние загрузки одним примитивом на весь
 * проект, чтобы каждый диалог с вводом выглядел и вёл себя одинаково.
 *
 * @family form-dialog
 * @when Пользователь принимает решение с вводом в модальном слое и подтверждает
 *   его запросом: действие отступа стоит в начале строки действий, главное — в
 *   конце, между ними — пояснение к недоступному действию; пока запрос идёт,
 *   слой не закрывается ни оверлеем, ни Escape. Для решения без ввода — семейство
 *   confirm-dialog, для длительной операции с прогрессом — progress-dialog.
 * @role Dialog
 * @parts title, subtitle, body, actions
 * @tokens none
 * @own
 * @variants none
 * @sizes sm|md
 * @states open|loading
 */
interface FormDialogProps {
  open: boolean;
  title: string;
  /** Контекст решения под заголовком; null — заголовок сам называет решение и подзаголовка нет. */
  subtitle: string | null;
  children: ReactNode;
  /** Действие отступа в начале строки действий; null — у диалога единственное действие. */
  startAction: ReactNode | null;
  /** Главное действие в конце строки действий. */
  endAction: ReactNode;
  /** Пояснение между действиями — причина недоступности одного из них; null — пояснять нечего. */
  actionsNote: string | null;
  /** Запрос по подтверждению идёт: закрытие оверлеем и Escape не срабатывает, слой помечен aria-busy. */
  loading: boolean;
  /** Ширина слоя: sm — подтверждение с одним вопросом, md — форма с полями и таблицами. */
  size: "sm" | "md";
  onClose: () => void;
}

export function FormDialog(props: FormDialogProps): JSX.Element {
  const { open, title, subtitle, children, startAction, endAction, actionsNote, loading, size, onClose } =
    props;
  const titleId = useId();
  const subtitleId = useId();
  const describedBy = subtitle === null ? undefined : subtitleId;
  // Два шага ширины семейства на шкале Dialog MUI: sm — вопрос с двумя действиями,
  // md — форма с полями и таблицами.
  const paperMaxWidth = size === "sm" ? "xs" : "sm";

  // Dialog MUI сам рисует оверлей (Backdrop) и панель с role="dialog" и aria-modal;
  // оверлей (backdropClick) и Escape (escapeKeyDown) — дополнительные выходы к
  // явному действию отступа; во время запроса они закрыты, потому что прервать
  // транзакцию нельзя.
  const handleClose = () => {
    if (!loading) {
      onClose();
    }
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth={paperMaxWidth}
      fullWidth
      aria-modal="true"
      aria-labelledby={titleId}
      aria-describedby={describedBy}
      slotProps={{ paper: { "aria-busy": loading } }}
    >
      <DialogTitle id={titleId} data-part="title">{title}</DialogTitle>
      <DialogContent data-part="body">
        {subtitle !== null && (
          <DialogContentText id={subtitleId} data-part="subtitle" sx={{ mb: 2 }}>
            {subtitle}
          </DialogContentText>
        )}
        {children}
      </DialogContent>
      <DialogActions data-part="actions">
        <Box display="flex" alignItems="center" gap={2} flexGrow={1}>
          {startAction}
          {actionsNote !== null && (
            <Typography variant="body2" color="text.secondary">
              {actionsNote}
            </Typography>
          )}
        </Box>
        {endAction}
      </DialogActions>
    </Dialog>
  );
}
